import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Gift, Sparkles, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import {
  DISCOUNTS,
  DiscountCode,
  getRewards,
  hasShownPopup,
  incrementVisitOnce,
  isReturningVisitor,
  markPopupShown,
  trackPageView,
  unlockCode,
} from "@/lib/rewards";

type RewardsCtx = {
  showDiscount: (code: keyof typeof DISCOUNTS, opts?: { force?: boolean }) => void;
  unlocked: string[];
  refresh: () => void;
};

const Ctx = createContext<RewardsCtx | null>(null);

export const useRewards = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useRewards must be used within RewardsProvider");
  return ctx;
};

const HIDDEN_ROUTES = ["/thank-you", "/thanks", "/success", "/form-success"];

export const RewardsProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [active, setActive] = useState<DiscountCode | null>(null);
  const [unlocked, setUnlocked] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const queue = useRef<string[]>([]);

  const refresh = useCallback(() => {
    setUnlocked(getRewards().unlocked);
  }, []);

  const showDiscount = useCallback(
    (codeKey: keyof typeof DISCOUNTS, opts?: { force?: boolean }) => {
      const code = DISCOUNTS[codeKey];
      if (!code) return;
      const popupId = `popup_${code.code}`;
      if (!opts?.force && hasShownPopup(popupId)) return;
      // Queue if one is already showing
      if (active) {
        queue.current.push(codeKey as string);
        return;
      }
      setActive(code);
      unlockCode(code.code);
      markPopupShown(popupId);
      refresh();
    },
    [active, refresh],
  );

  const closePopup = () => {
    setActive(null);
    setCopied(false);
    setTimeout(() => {
      const next = queue.current.shift();
      if (next) showDiscount(next as keyof typeof DISCOUNTS);
    }, 600);
  };

  // Initial setup — track visit only (no auto-popup discounts)
  useEffect(() => {
    incrementVisitOnce();
    refresh();
  }, [refresh]);

  // Track page views only (no auto-unlock discounts)
  useEffect(() => {
    if (HIDDEN_ROUTES.includes(location.pathname)) return;
    trackPageView(location.pathname);
  }, [location.pathname]);

  const copy = async () => {
    if (!active) return;
    try {
      await navigator.clipboard.writeText(active.code);
      setCopied(true);
      toast.success("Code copied!", { description: `${active.code} ready to paste at checkout.` });
    } catch {
      toast.error("Could not copy. Please copy manually.");
    }
  };

  const value = useMemo(() => ({ showDiscount, unlocked, refresh }), [showDiscount, unlocked, refresh]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
              onClick={closePopup}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <motion.div
              className="relative w-full max-w-md pointer-events-auto"
              initial={{ y: 60, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0, scale: 0.95 }}
              transition={{ type: "spring", damping: 22, stiffness: 220 }}
            >
              {/* Glow halo */}
              <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-primary/60 via-primary/10 to-primary/40 blur-md opacity-70" />
              <div className="relative rounded-3xl bg-gradient-to-b from-card to-background border border-primary/30 p-6 sm:p-8 overflow-hidden shadow-2xl">
                {/* Ambient glow */}
                <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-primary/20 blur-3xl pointer-events-none" />

                <button
                  onClick={closePopup}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-secondary/80 hover:bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground transition"
                  aria-label="Close"
                >
                  <X size={14} />
                </button>

                <div className="relative">
                  <motion.div
                    className="inline-flex items-center justify-center w-14 h-14 rounded-2xl gold-gradient-bg shadow-lg shadow-primary/40 mb-4"
                    initial={{ rotate: -20, scale: 0 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{ delay: 0.15, type: "spring", damping: 14 }}
                  >
                    <Gift size={26} className="text-primary-foreground" />
                  </motion.div>

                  <p className="text-[10px] uppercase tracking-[0.25em] text-primary font-semibold mb-2 flex items-center gap-1.5">
                    <Sparkles size={11} /> {active.label} Unlocked
                  </p>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight mb-2">
                    <span className="gold-gradient-text-animated">{active.percent}% off</span> your detail
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">{active.reason}.</p>
                  <p className="text-[11px] text-primary mb-4 font-medium">
                    Copy this code, then paste it into the booking form's <span className="font-semibold">message field</span> so we can apply your discount.
                  </p>

                  <button
                    onClick={copy}
                    className="group w-full flex items-center justify-between gap-3 rounded-xl border border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 transition px-4 py-3"
                  >
                    <div className="text-left">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Code</p>
                      <p className="font-mono text-lg font-bold tracking-wider text-foreground">{active.code}</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? "Copied" : "Copy"}
                    </span>
                  </button>

                  {active.proofRequired && (
                    <p className="text-[11px] text-muted-foreground mt-3 italic">
                      You'll be asked for proof ({active.proofType === "veteran" ? "DD-214, VA card, or military ID" : "valid ID with date of birth"}) at booking.
                    </p>
                  )}

                  <div className="flex gap-2 mt-5">
                    <a
                      href="/book"
                      className="flex-1 inline-flex items-center justify-center gold-gradient-bg text-primary-foreground px-4 py-3 text-sm font-semibold rounded-xl shadow-lg shadow-primary/30 hover:shadow-primary/50 transition"
                    >
                      Book & Apply Code
                    </a>
                    <button
                      onClick={closePopup}
                      className="px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition"
                    >
                      Later
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Ctx.Provider>
  );
};
